import React, { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "./DashboardLayout";
import { api, formatDateMX } from "../../lib/apiClient";
import { toast } from "sonner";
import { Save, Trash2, Plus, X } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

const WEEKDAYS = [["Lunes", 0], ["Martes", 1], ["Miércoles", 2], ["Jueves", 3], ["Viernes", 4], ["Sábado", 5], ["Domingo", 6]];

export default function Disponibilidad() {
  const [s, setS] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [nb, setNb] = useState({ start_date: "", end_date: "", reason: "" });

  const loadSettings = useCallback(() => api.get("/settings").then((r) => setS(r.data)).catch(() => {}), []);
  const loadBlocked = useCallback(() => api.get("/blocked-dates").then((r) => setBlocked(r.data)).catch(() => {}), []);
  const loadAll = useCallback(() => { loadSettings(); loadBlocked(); }, [loadSettings, loadBlocked]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useAdminRealtime(loadAll, ["settings", "availability"]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const toggleRest = (n) => { const cur = s.rest_days || []; set("rest_days", cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]); };

  const save = async () => { await api.put("/settings", s); toast.success("Disponibilidad guardada 🌸"); };

  const addSlot = () => { if (!newSlot) return; set("time_slots", [...(s.time_slots || []), newSlot].sort()); setNewSlot(""); };
  const removeSlot = (t) => set("time_slots", (s.time_slots || []).filter((x) => x !== t));

  const addBlocked = async () => {
    if (!nb.start_date) { toast.error("Elige una fecha inicial."); return; }
    const end = nb.end_date || nb.start_date;
    if (end < nb.start_date) { toast.error("La fecha final no puede ser menor."); return; }
    const { data } = await api.post("/blocked-dates/range", { start_date: nb.start_date, end_date: end, reason: nb.reason, full_day: true });
    setNb({ start_date: "", end_date: "", reason: "" }); loadBlocked();
    toast.success(data.count > 1 ? `${data.count} fechas bloqueadas` : "Fecha bloqueada");
  };
  const delBlocked = async (id) => { await api.delete(`/blocked-dates/${id}`); loadBlocked(); };

  if (!s) return <DashboardLayout title="Disponibilidad"><p className="text-cafe/50">Cargando…</p></DashboardLayout>;

  return (
    <DashboardLayout title="Disponibilidad">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-blancoCalido border border-beige rounded-[24px] p-4 sm:p-6 shadow-pastel space-y-4 min-w-0">
          <h3 className="font-serif text-xl text-vino">Horarios y capacidad</h3>
          <div>
            <label className="text-sm font-medium text-vino mb-2 block">Días de descanso</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(([label, n]) => (
                <button key={n} onClick={() => toggleRest(n)} data-testid={`rest-${n}`}
                  className={`text-xs px-3 py-2 rounded-full border-2 ${(s.rest_days || []).includes(n) ? "bg-vino text-crema border-vino" : "border-beige text-cafe/60"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-vino mb-1 block">Días mín. anticipación</label>
              <input data-testid="min-advance" type="number" value={s.min_advance_days || 0} onChange={(e) => set("min_advance_days", parseInt(e.target.value) || 0)} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
            <div><label className="text-sm font-medium text-vino mb-1 block">Máx. pedidos por día</label>
              <input data-testid="max-orders" type="number" value={s.max_orders_per_day || 0} onChange={(e) => set("max_orders_per_day", parseInt(e.target.value) || 0)} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
          </div>
          <div>
            <label className="text-sm font-medium text-vino mb-2 block">Horarios disponibles</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(s.time_slots || []).map((t) => (
                <span key={t} className="text-xs bg-beige text-vino rounded-full pl-3 pr-1 py-1 flex items-center gap-1">{t}<button onClick={() => removeSlot(t)} className="p-0.5"><X className="w-3 h-3" /></button></span>
              ))}
            </div>
            <div className="grid grid-cols-1 min-[380px]:grid-cols-[1fr_auto] gap-2">
              <input type="time" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} data-testid="new-slot" className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" />
              <button onClick={addSlot} data-testid="add-slot" className="w-full min-[380px]:w-auto justify-center bg-vino text-crema rounded-xl px-4 py-2 text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Agregar</button>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-vino mb-1 block">Mensaje para fechas no disponibles</label>
            <textarea data-testid="unavailable-msg" value={s.unavailable_message || ""} onChange={(e) => set("unavailable_message", e.target.value)} rows={2} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" />
          </div>
          <button onClick={save} data-testid="disp-save" className="w-full justify-center bg-vino text-crema rounded-full px-6 py-3 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Guardar cambios</button>
        </div>

        <div className="bg-blancoCalido border border-beige rounded-[24px] p-4 sm:p-6 shadow-pastel min-w-0">
          <h3 className="font-serif text-xl text-vino mb-4">Fechas bloqueadas</h3>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2 items-end mb-4">
            <div><label className="text-xs text-cafe/60">Desde</label><input data-testid="block-start-date" type="date" value={nb.start_date} onChange={(e) => setNb({ ...nb, start_date: e.target.value, end_date: nb.end_date || e.target.value })} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
            <div><label className="text-xs text-cafe/60">Hasta</label><input data-testid="block-end-date" type="date" value={nb.end_date} min={nb.start_date || undefined} onChange={(e) => setNb({ ...nb, end_date: e.target.value })} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
            <div className="min-[420px]:col-span-2"><label className="text-xs text-cafe/60">Motivo</label><input data-testid="block-reason" value={nb.reason} onChange={(e) => setNb({ ...nb, reason: e.target.value })} placeholder="Vacaciones, evento, descanso…" className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 focus:outline-none focus:border-vino" /></div>
            <button onClick={addBlocked} data-testid="add-block" className="min-[420px]:col-span-2 bg-vino text-crema rounded-xl px-4 py-2 text-sm">Bloquear rango</button>
          </div>
          <div className="space-y-2">
            {blocked.map((b) => (
              <div key={b.id} className="flex justify-between items-center bg-crema border border-beige rounded-xl p-3">
                <div><p className="text-sm font-medium text-vino">{formatDateMX(b.date)}</p>{b.reason && <p className="text-xs text-cafe/50">{b.reason}</p>}</div>
                <button onClick={() => delBlocked(b.id)} data-testid={`del-block-${b.id}`} className="text-vino p-1.5 hover:bg-beige rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {blocked.length === 0 && <p className="text-cafe/50 text-sm">Sin fechas bloqueadas.</p>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
